import ReactDOM from "react-dom";
import { useCallback, useEffect, useRef, useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { paymentStatsStyles } from "../styles/styles";
import CustomTooltip from "../../../Components/CustomTooltip";
import endpoints from "../../../Helpers/Endpoints";
import { apiGetRequest } from "../../../Helpers/Requests";
import { AppContext } from "../../../Context/AppContext";
import { useSocketContext } from "../../../Context/SocketContext/SocketProvider";
import { DONE_SERVICE_STATUS_NAMES } from "../../../Constants/serviceOrderStatus";
import useServiceStatusMap from "../../../hooks/useServiceStatusMap";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";
import { useTranslation } from "react-i18next";

function PaymentStats() {
  const { t } = useTranslation("page-my-jobs");
  const [container, setContainer] = useState(null);
  const [paymentStats, setPaymentStats] = useState({});
  const { profileData } = useContext(AppContext);

  const getPaymentStats = useCallback(async () => {
    try {
      const response = await apiGetRequest(
        endpoints.getSupplierJobsStats(profileData.id)
      );
      const data = response.data;
      setPaymentStats(data);
    } catch (error) {
      console.error(error);
    }
  }, [profileData]);

  const {
    root,
    section,
    sectionWithBorder,
    iconBoxPending,
    iconBoxEarnings,
    textContainer,
    titleRow,
    titleText,
    amountText,
    iconWrapper,
  } = paymentStatsStyles;

  useEffect(() => {
    const target = document.getElementById("header-button-box");
    setContainer(target);
    getPaymentStats();
  }, [getPaymentStats]);

  // Real-time: a service order changed status — most visibly the member paying,
  // which moves money from Pending payments to Total earnings. Without this the
  // figures stayed stale until the page was reloaded, even though the job
  // itself had already jumped to Completed (COMY1-334). Both figures are
  // service-only, so the catalog channels are not worth listening to.
  const { socket } = useSocketContext();
  // Status ids are DB rows; the payload carries ids, so translate them
  // through the fetched map rather than hardcoding any (COMY1-283).
  const serviceStatusMap = useServiceStatusMap();
  const serviceStatusMapRef = useRef(serviceStatusMap);
  useEffect(() => {
    serviceStatusMapRef.current = serviceStatusMap;
  }, [serviceStatusMap]);
  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => {
      // PAYMENT_DONE → CLOSED (the member's feedback survey) leaves both
      // figures exactly as they were — the money already counted as earnings.
      const { nameById } = serviceStatusMapRef.current;
      if (
        DONE_SERVICE_STATUS_NAMES.includes(nameById[Number(payload?.old_status_id)]) &&
        DONE_SERVICE_STATUS_NAMES.includes(nameById[Number(payload?.new_status_id)])
      ) {
        return;
      }
      getPaymentStats();
    };
    socket.on("service_order_status_update", handler);
    return () => socket.off("service_order_status_update", handler);
  }, [socket, getPaymentStats]);

  if (!container) return null;

  return ReactDOM.createPortal(
    <Box sx={root}>
      {/* Pending Payments */}
      <Box sx={section}>
        <Box sx={iconBoxPending}>
          <img
            src="/icons/time-quarter-icon.svg"
            alt="time-quarter-icon"
            style={{ width: "24px", height: "24px" }}
          />
        </Box>

        <Box sx={textContainer}>
          <Box sx={titleRow}>
            <Typography sx={titleText}>{t("pending_payments")}</Typography>
            <CustomTooltip
              title={t("pending_payments_tooltip")}
              tooltipSxStyles={{ paddingY: "5px" }}
            >
              <Box sx={iconWrapper}>
                <img
                  src="/icons/question-circle-icon.svg"
                  alt="question-circle-icon"
                  style={{ width: "16px", height: "16px" }}
                />
              </Box>
            </CustomTooltip>
          </Box>

          <Typography sx={amountText}>
            {Object.keys(paymentStats).length > 0
              ? getCurrencySymbol(paymentStats.currency) +
                (paymentStats.pending_payments?.toLocaleString("en-US") || 0)
              : "-"}
          </Typography>
        </Box>
      </Box>

      {/* Total Earnings */}
      <Box sx={sectionWithBorder}>
        <Box sx={iconBoxEarnings}>
          <img
            src="/icons/dollar-box-icon.svg"
            alt="dollar-box-icon"
            style={{ width: "24px", height: "24px" }}
          />
        </Box>

        <Box sx={textContainer}>
          <Box sx={titleRow}>
            <Typography sx={titleText}>{t("total_earnings")}</Typography>
            <CustomTooltip
              title={t("total_earnings_tooltip")}
              tooltipSxStyles={{ paddingY: "5px" }}
            >
              <Box sx={iconWrapper}>
                <img
                  src="/icons/question-circle-icon.svg"
                  alt="question-circle-icon"
                  style={{ width: "16px", height: "16px" }}
                />
              </Box>
            </CustomTooltip>
          </Box>

          <Typography sx={amountText}>
            {Object.keys(paymentStats).length > 0
              ? getCurrencySymbol(paymentStats.currency) +
                (paymentStats.total_earnings?.toLocaleString("en-US") || 0)
              : "-"}
          </Typography>
        </Box>
      </Box>
    </Box>,
    container
  );
}

export default PaymentStats;
